<template>
    <!-- Two grid items, not one wrapping div — lets the terminal-state chips below stretch (w-full) to match the Link/dismiss pair's width -->
    <div :class="['flex min-w-0 items-center gap-3', { 'py-3': !compact }]">
        <CollaborationProfileAvatar :size="compact ? 'md' : 'sm'" :name="suggestion.name" :available="suggestion.action === 'link'" />
        <div class="min-w-0">
            <p :class="['truncate text-ink', compact ? 'body-small' : 'nadia-chat-semibold']">{{ suggestion.name }}</p>
            <p v-if="compact && suggestion.title" class="truncate body-small text-ink-muted">{{ suggestion.title }}</p>
            <p v-else-if="!compact" class="truncate scaffold-details-small text-ink-muted">{{ subtext }}</p>
        </div>
    </div>

    <div :class="['suggestion-actions group flex items-center justify-end gap-2', { 'py-3': !compact }]">
        <!-- Compact dismiss (link suggestions only): revealed when the action area is hovered or
             keyboard-focused (always shown on touch devices), left of the action button. Kept in layout (opacity, not v-if) so the
             action button doesn't shift. -->
        <BaseButton
            v-if="compact && status === 'idle' && suggestion.action === 'link'"
            theme="tertiary"
            size="sm"
            icon-only
            :title="`Dismiss ${suggestion.name}`"
            :aria-label="`Dismiss ${suggestion.name}`"
            class="shrink-0 opacity-0 transition-[color,background-color,opacity] group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
            :disabled="isBusy || disabled"
            :data-test-id="`profile-dismiss-btn-${suggestion.target_user_id}`"
            @click="handleDismiss">
            <LoadingSpinner v-if="isDismissing" class="size-3.5" />
            <i v-else class="bi bi-trash3 text-xl" aria-hidden="true" />
        </BaseButton>
        <BaseButton
            v-if="compact"
            theme="tertiary"
            size="sm"
            icon-only
            :aria-label="
                status === 'shared'
                    ? `Copied profile link for ${suggestion.name}`
                    : suggestion.action === 'link'
                      ? `Link ${suggestion.name}`
                      : `Copy profile link for ${suggestion.name}`
            "
            :title="status === 'shared' ? 'Copied' : undefined"
            :disabled="isBusy || disabled || status === 'shared'"
            :data-test-id="`profile-${suggestion.action === 'link' ? 'link' : 'request'}-btn-${suggestion.target_user_id}`"
            @click="suggestion.action === 'link' ? handleLink() : handleShare()">
            <LoadingSpinner v-if="isBusy" class="size-4" />
            <i v-else-if="status === 'shared'" class="bi bi-check-circle-fill text-xl" aria-hidden="true" />
            <i v-else-if="suggestion.action === 'link'" class="bi bi-plus-circle text-xl" aria-hidden="true" />
            <i v-else class="bi bi-chat-left text-xl" aria-hidden="true" />
        </BaseButton>

        <StatusChip v-else-if="status === 'linked'" state="done" size="md" class="w-full justify-center">
            <template #leading><i class="bi bi-check-lg" aria-hidden="true" /></template>
            Linked
        </StatusChip>
        <StatusChip v-else-if="status === 'shared'" state="neutral" size="md" class="w-full justify-center text-ink-muted">
            <template #leading><i :class="shareViaTeams ? 'bi bi-send' : 'bi bi-clipboard-check'" aria-hidden="true" /></template>
            {{ sharedTerminalLabel }}
        </StatusChip>

        <BaseButton
            v-else-if="suggestion.action === 'link'"
            theme="primary"
            class="relative flex flex-1 items-center justify-center"
            :disabled="isBusy || disabled"
            :data-test-id="`profile-link-btn-${suggestion.target_user_id}`"
            @click="handleLink">
            <span :class="{ invisible: isLinking }">Link</span>
            <span v-if="isLinking" class="absolute inset-0 flex items-center justify-center">
                <LoadingSpinner class="size-4" />
            </span>
        </BaseButton>

        <BaseButton
            v-else
            theme="neutral"
            class="relative flex flex-1 items-center justify-center rounded-full"
            :disabled="isBusy || disabled"
            :data-test-id="`profile-request-btn-${suggestion.target_user_id}`"
            @click="handleShare">
            <span :class="{ invisible: isGeneratingLink }" class="inline-flex items-center gap-2">
                <img v-if="orgHasMsTeams" :src="msTeamsIcon" alt="" class="size-5 shrink-0" />
                <i v-else class="bi bi-link-45deg text-lg/none" aria-hidden="true"></i>
                {{ shareButtonLabel }}
            </span>
            <span v-if="isGeneratingLink" class="absolute inset-0 flex items-center justify-center">
                <LoadingSpinner class="size-4" />
            </span>
        </BaseButton>
        <BaseButton
            v-if="status === 'idle' && !compact"
            type="button"
            theme="tertiary"
            size="sm"
            icon-only
            :title="`Dismiss ${suggestion.name}`"
            :aria-label="`Dismiss ${suggestion.name}`"
            :disabled="isBusy || disabled"
            :data-test-id="`profile-dismiss-btn-${suggestion.target_user_id}`"
            @click="handleDismiss">
            <LoadingSpinner v-if="isDismissing" class="size-3.5" />
            <i v-else class="bi bi-trash3 text-base/none" aria-hidden="true"></i>
        </BaseButton>
    </div>
</template>

<script setup lang="ts">
import msTeamsIcon from "~img/ms-teams-icon.png";
import { useFetch } from "~vue/api";
import type { UpdateSuggestionActionRequest } from "~vue/api/generated/connections";
import { copyShareUrl, openTeamsShare } from "~vue/CollaborationProfile/composables/useProfileShareActions";
import { useProfileShareLink } from "~vue/CollaborationProfile/composables/useProfileShareLink";
import { type ProfileShareSource, withShareAttribution } from "~vue/CollaborationProfile/profileShareAttribution";
import BaseButton from "~vue/components/BaseButton.vue";
import CollaborationProfileAvatar from "~vue/components/CollaborationProfileAvatar.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { firstNameOf } from "~vue/utils/stringUtils";
import { computed, ref } from "vue";

import type { CollabProfileLinkRequest, OwnProfileShare, ProfileLinkSuggestion, ShareFailureReason, ShareTarget, SuggestionStatus } from "./profileLinkTypes";

const props = defineProps<{
    suggestion: ProfileLinkSuggestion;
    status: SuggestionStatus;
    ownProfileShare: OwnProfileShare | null;
    orgHasMsTeams: boolean;
    shareSource: ProfileShareSource;
    disabled?: boolean;
    compact?: boolean;
}>();

const emit = defineEmits<{
    linked: [suggestion: ProfileLinkSuggestion];
    "link-failed": [suggestion: ProfileLinkSuggestion];
    shared: [suggestion: ProfileLinkSuggestion, target: ShareTarget];
    "share-failed": [suggestion: ProfileLinkSuggestion, reason: ShareFailureReason];
    "dismiss-started": [suggestion: ProfileLinkSuggestion];
    dismissed: [suggestion: ProfileLinkSuggestion];
    "dismiss-failed": [suggestion: ProfileLinkSuggestion];
}>();

const linkUrl = computed(() => `/v1/collaboration-profiles/${props.suggestion.target_user_id}/ensure-link`);
const linkPayload = computed<CollabProfileLinkRequest>(() => ({ connection_id: props.suggestion.connection_id }));
const { error: linkError, isFetching: isLinking, execute: executeLink } = useFetch(linkUrl, { immediate: false }).post(linkPayload).json();

const dismissPayload = ref<UpdateSuggestionActionRequest>();
// The action endpoint returns 204 No Content: .json() would throw on the empty body, and useFetch
// parses before checking response.ok, so a committed dismissal would be reported as a failure.
const {
    error: dismissError,
    statusCode: dismissStatus,
    isFetching: isDismissing,
    execute: executeDismiss,
} = useFetch("/v1/connections/profile-link-suggestions/action", { immediate: false }).post(dismissPayload);

const { createTargetedInvitation, isGeneratingLink } = useProfileShareLink({
    profileId: computed(() => props.ownProfileShare?.profile_id),
});

const isBusy = computed(() => isLinking.value || isGeneratingLink.value || isDismissing.value);

async function handleLink() {
    if (isBusy.value) {
        return;
    }
    await executeLink();

    // Includes the stale-suggestion race (profile no longer viewable → ensure-link 404).
    if (linkError.value) {
        emit("link-failed", props.suggestion);
        return;
    }
    emit("linked", props.suggestion);
}

const shareViaTeams = computed(() => props.orgHasMsTeams && !props.compact);

async function handleShare() {
    if (isBusy.value) {
        return;
    }
    if (!props.ownProfileShare?.can_share) {
        emit("share-failed", props.suggestion, "not_publishable");
        return;
    }

    const shareUrl = await createTargetedInvitation(props.suggestion.target_user_id);
    if (!shareUrl) {
        emit("share-failed", props.suggestion, "invite");
        return;
    }

    const message = `Hi ${firstNameOf(props.suggestion.name)}! Here's my Collaboration Profile, including my Perspective results when available, so you can see how I like to work. Share yours back so I can do the same?`;
    if (shareViaTeams.value) {
        openTeamsShare(withShareAttribution(shareUrl, props.shareSource, "teams"), message);
        emit("shared", props.suggestion, "teams");
        return;
    }

    if (!(await copyShareUrl(withShareAttribution(shareUrl, props.shareSource, "link"), message))) {
        emit("share-failed", props.suggestion, "clipboard");
        return;
    }
    emit("shared", props.suggestion, "link");
}

async function handleDismiss() {
    if (isBusy.value) {
        return;
    }
    dismissPayload.value = { target_user_id: props.suggestion.target_user_id, action: "dismissed" };
    emit("dismiss-started", props.suggestion);
    await executeDismiss();

    if (dismissError.value || dismissStatus.value === null) {
        emit("dismiss-failed", props.suggestion);
        return;
    }
    emit("dismissed", props.suggestion);
}

const shareButtonLabel = computed(() => (shareViaTeams.value ? "Send" : "Copy"));
const sharedTerminalLabel = computed(() => (shareViaTeams.value ? "Sent" : "Copied"));

const subtext = computed(() => {
    const { title, source, meeting_count: meetingCount } = props.suggestion;
    if (source === "profile_linker") {
        return title ? `${title} · Linked your collaboration profile` : "Linked your collaboration profile";
    }
    if (source === "calendar") {
        return `From calendar · ${meetingCount === 1 ? "1 meeting" : `${meetingCount} meetings`}`;
    }
    return title ? `${title} · In your people` : "In your people";
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /* Bootstrap glyphs carry a baseline offset that reads as low inside the square icon buttons;
       flex-center them so they sit dead-center. */
    .suggestion-actions :deep(button > .bi) {
        @apply flex items-center justify-center;
    }
}
</style>
